import type { StoreAdmin, StoreRole } from "@hobbyshopify/backend-client";
import { BaseController, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import {
  addStoreAdmin,
  changeStoreAdminRole,
  loadStoreAdmins,
  removeStoreAdmin,
} from "../../../../core/ops/store-admin-ops";
import { errorText } from "../../../../core/primitives/errors";
import { failedLoad, loaded, loading } from "../../../../core/primitives/load-status";
import { assignableRoles, canChangeRole, canRemove, roleLabel } from "../../../../core/primitives/store-role";
import { type AdminMember, ShopAdminsSectionActions, ShopAdminsSectionState } from "./shop-admins-section-model";

export type ShopAdminsSectionDeps = Pick<AppDeps, "client" | "authHandler">;

export interface ShopAdminsSectionHost {
  readonly storeId: string;
  /** The caller's own role in this shop. */
  readonly role: StoreRole;
}

/** Who else can run this shop, and at what role. See `core/primitives/store-role.ts` for the matrix. */
export class ShopAdminsSectionController extends BaseController implements ShopAdminsSectionActions {
  readonly state = new ShopAdminsSectionState();

  constructor(
    subtree: SubtreeModel,
    readonly deps: ShopAdminsSectionDeps,
    readonly host: ShopAdminsSectionHost,
  ) {
    super();
    subtree.put(ShopAdminsSectionState, this.state);
    subtree.put(ShopAdminsSectionActions, this);
    this.state.assignableRoles.value = assignableRoles(host.role);
  }

  /** Loads once, the first time the tab is shown. */
  ensureLoaded(): void {
    if (this.state.status.value.kind !== "idle") return;
    void this.#load();
  }

  setEmail = (value: string) => {
    this.state.email.value = value;
  };

  setNewRole = (role: StoreRole) => {
    this.state.newRole.value = role;
  };

  add = async () => {
    const address = this.state.email.value.trim();
    if (!address || this.state.adding.value) return;
    this.state.error.value = null;
    this.state.added.value = null;
    this.state.adding.value = true;
    const role = this.state.newRole.value;
    const sameEmail = (a: StoreAdmin) => a.email.toLowerCase() === address.toLowerCase();
    const wasMember = this.state.members.value.some((m) => sameEmail(m.admin));
    const r = await addStoreAdmin({ ...this.deps, storeId: this.host.storeId, email: address, role });
    if (this.isDisposed) return;
    this.state.adding.value = false;
    if (!r.ok) {
      this.state.error.value = errorText(r.errors);
      return;
    }
    this.#setMembers(r.value);
    this.state.email.value = "";
    // The server keeps an existing member's role, so report the role the list now shows.
    const actualRole = r.value.find(sameEmail)?.role ?? role;
    this.state.added.value = wasMember
      ? `${address} is already a member as ${roleLabel(actualRole)}.`
      : `${address} was added as ${roleLabel(actualRole)}.`;
  };

  changeRole = async (admin: StoreAdmin, role: StoreRole) => {
    if (this.state.changingId.value) return;
    this.state.error.value = null;
    this.state.added.value = null;
    this.state.changingId.value = admin.id;
    const r = await changeStoreAdminRole({ ...this.deps, storeId: this.host.storeId, userId: admin.id, role });
    if (this.isDisposed) return;
    this.state.changingId.value = null;
    if (!r.ok) {
      this.state.error.value = errorText(r.errors);
      return;
    }
    this.#setMembers(r.value);
  };

  remove = async (admin: StoreAdmin) => {
    if (this.state.removingId.value) return;
    this.state.error.value = null;
    this.state.added.value = null;
    this.state.removingId.value = admin.id;
    const r = await removeStoreAdmin({ ...this.deps, storeId: this.host.storeId, userId: admin.id });
    if (this.isDisposed) return;
    this.state.removingId.value = null;
    if (!r.ok) {
      this.state.error.value = errorText(r.errors);
      return;
    }
    this.#setMembers(r.value);
  };

  async #load(): Promise<void> {
    this.state.status.value = loading;
    const r = await loadStoreAdmins({ ...this.deps, storeId: this.host.storeId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.#setMembers(r.value);
    this.state.status.value = loaded;
  }

  /** The last Owner may be neither removed nor demoted, whoever is asking. */
  #setMembers(admins: StoreAdmin[]): void {
    const ownerCount = admins.filter((a) => a.role === "OWNER").length;
    const members: AdminMember[] = admins.map((admin) => {
      const isLastOwner = admin.role === "OWNER" && ownerCount <= 1;
      return {
        admin,
        canRemove: canRemove(this.host.role, admin, ownerCount),
        canChangeRole: canChangeRole(this.host.role, admin) && !isLastOwner,
      };
    });
    this.state.members.value = members;
  }
}
